import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

// Desktop collapse state for the nav sidebar — a narrow icon rail instead of
// the full 256px column.
//
// It lives in context rather than inside Sidebar because the width is set in
// TWO places that must agree: Sidebar's own panel and the wrapper div in
// App.jsx that reserves its space in the flex row. Those are siblings, not
// parent and child, so neither can own the state for the other.
//
// Deliberately separate from DashboardThemeContext even though it mirrors its
// shape: the sidebar is mounted OUTSIDE the theme-scoped wrapper (it stays dark
// regardless of the dashboard's light/dark toggle), so folding this in would
// mean the sidebar consuming a context it is otherwise designed not to see.
//
// Mobile expansion (`mobileExpanded`) rides along here for the same reason the
// desktop flag does: below md the sidebar renders nothing until opened, so its
// trigger lives in the main content column — a sibling of Sidebar, not a child.
// It is kept SEPARATE from `collapsed` rather than reusing it, because
// `collapsed` persists to localStorage and defaults to false: sharing it would
// pop the panel open over the content on first load, and a phone tapping expand
// would rewrite the desktop preference (including the editor auto-collapse).
const STORAGE_KEY = "subscene-sidebar-collapsed";

// Routes that collapse the rail on entry, because they need the width more
// than they need the nav: the timeline editor runs a preview, a scene
// inspector and a horizontally-scrolling timeline side by side.
const AUTO_COLLAPSE = [/^\/workflows\/[^/]+\/editor\/?$/];

// Whether a path is one of those routes, for chrome that should stay out of
// their way (the floating mobile menu button).
export const isAutoCollapseRoute = (pathname) => AUTO_COLLAPSE.some((re) => re.test(pathname));

// The user's own choice, which only a deliberate toggle writes.
const readSavedCollapsed = () => {
  try {
    return localStorage.getItem(STORAGE_KEY) === "true";
  } catch {
    return false;
  }
};

const SidebarCollapseContext = createContext({
  collapsed: false,
  toggleCollapsed: () => {},
  mobileExpanded: false,
  setMobileExpanded: () => {},
});

export function SidebarCollapseProvider({ children }) {
  // Read synchronously in the initializer, like the theme provider, so the
  // first paint is already at the right width and the rail doesn't visibly
  // snap shut on load.
  const [collapsed, setCollapsed] = useState(readSavedCollapsed);

  // Session-only, deliberately not persisted — see the note above.
  const [mobileExpanded, setMobileExpanded] = useState(false);

  const { pathname } = useLocation();

  // Close the expanded mobile panel on navigation: it covers the content, so
  // leaving it open would hide the page just navigated to.
  useEffect(() => {
    setMobileExpanded(false);
  }, [pathname]);

  // Auto-collapse on ENTERING an editor route, not while sitting on one.
  //
  // Keyed on the path having actually changed: without this guard the effect
  // re-runs on every render the route is active and would slam the rail shut
  // again the moment a user expanded it mid-edit — turning their own toggle
  // into a no-op on exactly the screen it matters most. Leaving one of these
  // routes hands the rail back to the saved preference.
  const previousPath = useRef(null);
  useEffect(() => {
    const previous = previousPath.current;
    previousPath.current = pathname;
    if (previous === pathname) return;
    if (isAutoCollapseRoute(pathname)) {
      setCollapsed(true);
    } else if (previous && isAutoCollapseRoute(previous)) {
      setCollapsed(readSavedCollapsed());
    }
  }, [pathname]);

  const toggleCollapsed = useCallback(() => {
    setCollapsed((c) => {
      const next = !c;
      try {
        // Only a deliberate toggle is remembered. The route default above
        // writes nothing, so opening the editor once doesn't silently collapse
        // the sidebar everywhere else forever.
        localStorage.setItem(STORAGE_KEY, String(next));
      } catch {
        // Non-fatal — the toggle still works for this session.
      }
      return next;
    });
  }, []);

  return (
    <SidebarCollapseContext.Provider
      value={{ collapsed, toggleCollapsed, mobileExpanded, setMobileExpanded }}
    >
      {children}
    </SidebarCollapseContext.Provider>
  );
}

export const useSidebarCollapse = () => useContext(SidebarCollapseContext);
